import { ArrowUp, ArrowDown, Minus } from "@phosphor-icons/react";
import { AnimatedNumber } from "../primitives/AnimatedNumber";
import { AnimatedGauge, type GaugeThreshold } from "../primitives/AnimatedGauge";
import { cn } from "../../lib/cn";

type TrendDirection = "up" | "down" | "stable";

interface VitalGaugeProps {
  label: string;
  value: number;
  max?: number;
  format?: (n: number) => string;
  trend?: TrendDirection;
  showGauge?: boolean;
  thresholds?: GaugeThreshold[];
  className?: string;
}

const TREND_ICON = { up: ArrowUp, down: ArrowDown, stable: Minus };
const TREND_COLOR = {
  up: "text-[var(--color-signal-green)]",
  down: "text-[var(--color-soft-red)]",
  stable: "text-[var(--text-muted)]",
};

function VitalGauge({
  label,
  value,
  max = 100,
  format,
  trend,
  showGauge = true,
  thresholds,
  className,
}: VitalGaugeProps) {
  const TrendIcon = trend ? TREND_ICON[trend] : null;

  return (
    <div className={cn(
      "flex flex-col items-center gap-1 rounded-[var(--radius-md)] p-2",
      "bg-[var(--bg-surface)] border border-[var(--border-hairline)]",
      className,
    )}>
      {showGauge ? (
        <AnimatedGauge
          value={value}
          max={max}
          size={64}
          strokeWidth={5}
          format={format}
          thresholds={thresholds}
        />
      ) : (
        <div className="flex items-baseline gap-1">
          <AnimatedNumber
            value={value}
            format={format}
            className="text-lg font-semibold text-[var(--text-primary)]"
          />
          {TrendIcon && (
            <TrendIcon
              size={10}
              weight="bold"
              className={trend ? TREND_COLOR[trend] : ""}
            />
          )}
        </div>
      )}
      <span className="text-[9px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
        {label}
      </span>
    </div>
  );
}

VitalGauge.displayName = "VitalGauge";

export { VitalGauge };
export type { VitalGaugeProps, TrendDirection };
